import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, Printer, Send, CheckCircle, XCircle, Mail, MessageSquare, Edit3, Clock, AlertCircle, Link as LinkIcon, RotateCcw, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import CallSheetHtmlPreview from "../components/CallSheetHtmlPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

// Legacy inline preview kept for backwards compatibility – now delegates to shared component.
function HollywoodSheet({ cs, project, crewDb }) {
  return (
    <CallSheetHtmlPreview
      cs={cs}
      accentColor={project?.accent_color}
      logoDataUri={cs?.logo_data_uri || project?.logo_data_uri}
      logoAlign={project?.logo_align}
      logoMaxHeightPt={project?.logo_max_height_pt}
      isDraft={cs?.status === "draft"}
      crewDb={crewDb}
      deptOrder={project?.departments || []}
    />
  );
}

export default function CallSheetPreview() {
  const { callsheetId } = useParams();
  const navigate = useNavigate();
  const [cs, setCs] = useState(null);
  const [project, setProject] = useState(null);
  const [crew, setCrew] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [distributing, setDistributing] = useState(false);
  const [resending, setResending] = useState({});
  const [expanded, setExpanded] = useState({});
  const [logs, setLogs] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [showDist, setShowDist] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const fetchRsvps = () => axios.get(`${API}/callsheets/${callsheetId}/rsvps`, ax()).then(r => setRsvps(r.data)).catch(() => {});
  const fetchLogs = () => axios.get(`${API}/callsheets/${callsheetId}/distribution-logs`, ax()).then(r => setLogs(r.data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/callsheets/${callsheetId}`, ax()).then(r => {
      setCs(r.data);
      if (r.data?.project_id) {
        axios.get(`${API}/projects/${r.data.project_id}`, ax()).then(p => setProject(p.data)).catch(() => {});
        axios.get(`${API}/projects/${r.data.project_id}/crew`, ax()).then(c => setCrew(c.data)).catch(() => {});
      } else {
        axios.get(`${API}/crew`, ax()).then(c => setCrew(c.data)).catch(() => {});
      }
    }).catch(() => navigate("/dashboard"));
    axios.get(`${API}/callsheets/${callsheetId}/distribution-logs`, ax()).then(r => setLogs(r.data)).catch(() => {});
    fetchRsvps();
    const interval = setInterval(() => { fetchRsvps(); fetchLogs(); }, 15000);
    return () => clearInterval(interval);
  }, [callsheetId]);

  // Only crew/talent actually attached to THIS call sheet are eligible
  // recipients — the DB view would pull people who aren't on the day.
  const onSheetCrew = useMemo(() => {
    if (!cs) return [];
    const ids = new Set();
    (cs.crew_assignments || []).forEach(a => a?.crew_id && ids.add(a.crew_id));
    (cs.cast_calls || []).forEach(a => a?.crew_id && ids.add(a.crew_id));
    (cs.dept_calls || []).forEach(a => a?.crew_id && ids.add(a.crew_id));
    return crew.filter(m => ids.has(m.id));
  }, [cs, crew]);

  // Pre-select everyone on the call sheet when both lists load.
  const [autoSelected, setAutoSelected] = useState(false);
  useEffect(() => {
    if (autoSelected || !cs || onSheetCrew.length === 0) return;
    setSelected(onSheetCrew.map(m => m.id));
    setAutoSelected(true);
  }, [cs, onSheetCrew, autoSelected]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/callsheets/${callsheetId}/pdf`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `callsheet_${cs?.shoot_date || "unknown"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("PDF download failed"); }
    finally { setDownloading(false); }
  };

  const printSheet = () => window.print();

  const toggleAll = () => {
    if (selected.length === onSheetCrew.length) setSelected([]);
    else setSelected(onSheetCrew.map(c => c.id));
  };

  const distribute = async () => {
    if (!selected.length) return alert("Select at least one crew member");
    if (!sendEmail && !sendSms) return alert("Select email or SMS");
    setDistributing(true);
    try {
      const { data } = await axios.post(`${API}/callsheets/${callsheetId}/distribute`,
        { crew_ids: selected, send_email: sendEmail, send_sms: sendSms, message, subject },
        ax()
      );
      await fetchLogs();
      if (data.status) {
        setCs(prev => ({ ...prev, status: data.status }));
      }
      toast.success(`Sent to ${data.sent_count} recipient(s). Live delivery status appears below.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Distribution failed");
    } finally { setDistributing(false); }
  };

  const resendOne = async (crewId, type) => {
    const key = `${crewId}:${type}`;
    setResending(s => ({ ...s, [key]: true }));
    try {
      await axios.post(`${API}/callsheets/${callsheetId}/resend`,
        { crew_id: crewId, type, message, subject }, ax());
      await fetchLogs();
      toast.success(`Resent via ${type.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setResending(s => ({ ...s, [key]: false }));
    }
  };

  const copyPublicLink = async () => {
    if (!cs?.public_token) return toast.error("Publish the call sheet first to get a public link");
    const url = `${window.location.origin}/p/${cs.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied to clipboard");
    } catch {
      prompt("Copy this public link:", url);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await axios.post(`${API}/callsheets/${callsheetId}/publish`, {}, ax());
      setCs(data);
      toast.success("Call sheet published. The DRAFT watermark has been removed.");
    } catch (e) {
      alert(e.response?.data?.detail || "Publishing failed");
    } finally {
      setPublishing(false);
    }
  };

  // Group logs by (crew_id, type). For each recipient, surface the latest row
  // as the current status PLUS a flat timeline of every provider event across
  // all attempts (so a resend adds a fresh "sent" entry below the first one).
  const latestByRecipient = useMemo(() => {
    const map = new Map();
    for (const l of logs) {
      const key = `${l.crew_id || l.recipient}:${l.type}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    const rows = [];
    for (const [, group] of map) {
      // oldest → newest
      group.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
      const latest = group[group.length - 1];
      const timeline = [];
      for (const row of group) {
        const sendStatus = ["failed", "undelivered"].includes((row.status || "").toLowerCase())
          ? (row.status || "failed")
          : "sent";
        timeline.push({ event: sendStatus, ts: row.timestamp, reason: row.error || "", attempt: true });
        for (const ev of (row.events || [])) {
          timeline.push({ event: ev.event || "update", ts: ev.ts, reason: ev.reason || ev.response || "" });
        }
      }
      rows.push({ ...latest, _timeline: timeline, _attempts: group.length });
    }
    return rows.sort((a, b) => (b.updated_at || b.timestamp || "").localeCompare(a.updated_at || a.timestamp || ""));
  }, [logs]);

  const fmtEventTs = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return iso;
    }
  };
  const eventColor = (ev) => {
    const e = (ev || "").toLowerCase();
    if (["delivered", "opened", "clicked"].includes(e)) return "text-emerald-400";
    if (["failed", "bounce", "bounced", "dropped", "blocked", "spam", "spamreport", "undelivered"].includes(e)) return "text-red-400";
    if (["sending", "sent", "processed", "queued", "deferred"].includes(e)) return "text-amber-400";
    return "text-gray-400";
  };

  if (!cs) return <div className="flex items-center justify-center h-full"><div className="text-amber-400 animate-pulse">Loading...</div></div>;

  return (
    <div className="min-w-0 min-h-screen bg-[#0D0F12]">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 flex flex-col gap-3 border-b border-[#28303F] bg-[#0D0F12]/90 px-3 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-6 no-print">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => navigate(`/callsheets/${callsheetId}/edit`)}
            className="flex items-center gap-2 text-gray-500 hover:text-amber-400 text-sm transition-colors">
            <ArrowLeft size={15} /> Back
          </button>
          <span className="text-gray-700">|</span>
          <span className="text-white text-sm font-medium">{cs.production_name}</span>
          {cs.shoot_date && <span className="text-gray-500 text-sm">{cs.shoot_date}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="pdf-canvas-container">
          <button onClick={() => navigate(`/callsheets/${callsheetId}/edit`)}
            className="flex items-center gap-2 text-gray-400 hover:text-white border border-[#28303F] hover:border-gray-500 px-3 py-1.5 rounded-lg text-sm transition-all">
            <Edit3 size={14}/> Edit
          </button>
          <button data-testid="pdf-print-btn" onClick={printSheet}
            className="flex items-center gap-2 text-gray-400 hover:text-white border border-[#28303F] hover:border-gray-500 px-3 py-1.5 rounded-lg text-sm transition-all">
            <Printer size={14}/> Print
          </button>
          <button data-testid="pdf-download-btn" onClick={downloadPDF} disabled={downloading}
            className="flex items-center gap-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 px-3 py-1.5 rounded-lg text-sm transition-all disabled:opacity-50">
            <Download size={14}/> {downloading ? "..." : "PDF"}
          </button>
          {cs.status === "draft" ? (
            <button
              data-testid="callsheet-publish-btn"
              onClick={publish}
              disabled={publishing}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-1.5 rounded-lg text-sm transition-all disabled:opacity-50"
            >
              <CheckCircle size={14} /> {publishing ? "Publishing..." : "Publish"}
            </button>
          ) : (
            <>
              {cs.public_token && (
                <button data-testid="copy-public-link-btn" onClick={copyPublicLink}
                  className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 px-3 py-1.5 rounded-lg text-sm transition-all">
                  <LinkIcon size={14}/> Copy public link
                </button>
              )}
              <button data-testid="pdf-distribute-btn" onClick={() => navigate(`/callsheets/${callsheetId}/distribute`)}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-1.5 rounded-lg text-sm transition-all">
                <Send size={14}/> Distribute
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex min-w-0">
        {/* Call Sheet Preview */}
        <div className="flex min-w-0 flex-1 justify-center p-3 sm:p-6">
          <div
            className="call-sheet-preview-canvas print-area w-full overflow-auto rounded-md shadow-2xl"
            style={{ maxWidth: "8.5in" }}
          >
            <HollywoodSheet cs={cs} project={project} crewDb={crew} />
          </div>
        </div>

        {/* Distribution Panel — sticky viewport-height sidebar so the Send
            button is always in view regardless of how long the preview is. */}
        {showDist && (
          <div
            className="w-96 shrink-0 bg-[#14181F] border-l border-[#28303F] flex flex-col no-print self-start sticky"
            style={{ top: "56px", height: "calc(100vh - 56px)" }}
          >
            <div className="p-5 border-b border-[#28303F]">
              <h3 className="font-bold text-white font-[Outfit]">Distribute Call Sheet</h3>
              <p className="text-xs text-gray-500 mt-1">Select recipients and send via email or SMS</p>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Channel */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Send via</p>
                <div className="flex gap-2">
                  <button onClick={() => setSendEmail(!sendEmail)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${sendEmail ? "bg-blue-500/20 text-blue-400 border-blue-500/30" : "border-[#28303F] text-gray-500"}`}>
                    <Mail size={14}/> Email
                  </button>
                  <button onClick={() => setSendSms(!sendSms)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${sendSms ? "bg-green-500/20 text-green-400 border-green-500/30" : "border-[#28303F] text-gray-500"}`}>
                    <MessageSquare size={14}/> SMS
                  </button>
                </div>
              </div>

              {/* Optional message */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Custom Subject (optional)</label>
                <input value={subject} onChange={e => setSubject(e.target.value)}
                  placeholder={`Call Sheet: ${cs.production_name} – ${cs.shoot_date}`}
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Additional Note (optional)</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
                  placeholder="Any additional message to include..."
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-amber-500 resize-none" />
              </div>

              {/* Crew list — only talent/crew actually on this call sheet */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Recipients ({selected.length}/{onSheetCrew.length})</p>
                  <button onClick={toggleAll} className="text-xs text-amber-400 hover:text-amber-300">
                    {selected.length === onSheetCrew.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                {onSheetCrew.length === 0 ? (
                  <p className="text-gray-600 text-xs py-4 text-center">No crew or talent assigned to this call sheet yet.</p>
                ) : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {onSheetCrew.map(m => {
                      const isSelected = selected.includes(m.id);
                      // Call time: crew uses `crew_assignments.call_time`;
                      // talent uses `cast_calls.arrive_time` (the ARRIVE column).
                      const crewAsgn = (cs.crew_assignments || []).find(a => a.crew_id === m.id);
                      const castAsgn = (cs.cast_calls || []).find(a => a.crew_id === m.id);
                      const callTime = crewAsgn?.call_time || castAsgn?.arrive_time || "";
                      return (
                        <button key={m.id} data-testid="recipient-checkbox"
                          onClick={() => setSelected(s => s.includes(m.id) ? s.filter(x => x !== m.id) : [...s, m.id])}
                          className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-left transition-all ${isSelected ? "bg-amber-500/10 border-amber-500/30" : "border-[#28303F] hover:border-[#404854]"}`}>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-medium truncate ${isSelected ? "text-white" : "text-gray-400"}`}>{m.name}</p>
                            <p className="text-xs text-gray-600 truncate">{m.role} {m.email && `· ${m.email}`}</p>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            {callTime && <p className="text-xs text-amber-400 font-mono whitespace-nowrap">{callTime}</p>}
                            <div className={`w-4 h-4 rounded border-2 ml-auto mt-1 ${isSelected ? "bg-amber-500 border-amber-500" : "border-[#28303F]"}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 border-t border-[#28303F] space-y-3">
              <button data-testid="send-email-btn" onClick={distribute} disabled={distributing || !selected.length}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-3 rounded-lg text-sm transition-all">
                <Send size={15}/> {distributing ? "Sending..." : `Send to ${selected.length} Recipient(s)`}
              </button>
            </div>

            {/* RSVPs */}
            {rsvps.length > 0 && (
              <div className="p-5 border-t border-[#28303F]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Replies ({rsvps.filter(r => r.status !== "pending").length}/{rsvps.length})</p>
                  <button onClick={fetchRsvps} className="text-xs text-amber-400 hover:text-amber-300">Refresh</button>
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {rsvps.map((r, i) => {
                    const color = r.status === "confirmed" ? "text-green-400" :
                                  r.status === "declined" ? "text-red-400" : "text-gray-500";
                    const icon = r.status === "confirmed" ? <CheckCircle size={11}/> :
                                 r.status === "declined" ? <XCircle size={11}/> : <span className="w-2 h-2 rounded-full bg-gray-600 inline-block"/>;
                    return (
                      <div key={i} data-testid="rsvp-row" className="flex items-center justify-between text-xs bg-[#0D0F12] border border-[#28303F] rounded px-2.5 py-1.5">
                        <span className="text-gray-300">{r.crew_name}</span>
                        <span className={`${color} flex items-center gap-1 capitalize`}>{icon} {r.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Logs */}
            {latestByRecipient.length > 0 && (
              <div className="p-5 border-t border-[#28303F]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Delivery Status ({latestByRecipient.length})</p>
                  <button onClick={fetchLogs} className="text-xs text-amber-400 hover:text-amber-300">Refresh</button>
                </div>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {latestByRecipient.map((l) => {
                    const key = `${l.crew_id || l.recipient}:${l.type}`;
                    const status = (l.status || "").toLowerCase();
                    const isFailed = ["failed", "bounced", "dropped", "blocked", "spam", "undelivered"].includes(status);
                    const isDelivered = ["delivered", "opened", "clicked"].includes(status);
                    const isPending = ["sending", "sent", "queued", "deferred"].includes(status);
                    const color = isFailed ? "text-red-400" : isDelivered ? "text-emerald-400" : isPending ? "text-amber-400" : "text-gray-400";
                    const icon = isFailed ? <AlertCircle size={11}/> : isDelivered ? <CheckCircle size={11}/> : isPending ? <Clock size={11}/> : <XCircle size={11}/>;
                    const resendKey = `${l.crew_id}:${l.type}`;
                    const isResending = !!resending[resendKey];
                    const isOpen = !!expanded[key];
                    const timeline = l._timeline || [];
                    return (
                      <div key={key} data-testid="distribution-status-badge"
                        className="text-xs bg-[#0D0F12] border border-[#28303F] rounded">
                        <button
                          data-testid="delivery-row-toggle"
                          onClick={() => setExpanded(s => ({ ...s, [key]: !s[key] }))}
                          className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left hover:bg-white/[0.02] transition-colors">
                          <div className="flex min-w-0 flex-1 items-center gap-1.5">
                            {isOpen ? <ChevronDown size={12} className="text-gray-500 shrink-0"/> : <ChevronRight size={12} className="text-gray-500 shrink-0"/>}
                            <div className="min-w-0 flex-1">
                              <p className="text-gray-200 truncate">
                                {l.name}
                                <span className="text-gray-600"> ({l.type})</span>
                                {l._attempts > 1 && <span className="ml-1 text-[9px] text-gray-500">· {l._attempts} attempts</span>}
                              </p>
                              <p className="text-[10px] text-gray-600 truncate">{l.recipient}</p>
                              {isFailed && l.error && !isOpen && <p className="text-[10px] text-red-500/80 truncate" title={l.error}>Reason: {l.error}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                            <span className={`${color} flex items-center gap-1 capitalize`}>{icon} {status || "unknown"}</span>
                            {isFailed && l.crew_id && (
                              <button
                                data-testid="resend-recipient-btn"
                                onClick={() => resendOne(l.crew_id, l.type)}
                                disabled={isResending}
                                className="flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-bold text-amber-400 transition-all hover:bg-amber-500/20 disabled:opacity-50">
                                <RotateCcw size={10}/> {isResending ? "..." : "Resend"}
                              </button>
                            )}
                          </div>
                        </button>
                        {isOpen && (
                          <div data-testid="delivery-timeline" className="border-t border-[#28303F] bg-[#0A0C10] px-3 py-2">
                            {timeline.length === 0 ? (
                              <p className="text-[10px] text-gray-600">No events recorded yet.</p>
                            ) : (
                              <ol className="relative space-y-2 pl-3">
                                {timeline.map((ev, i) => (
                                  <li key={i} className="relative">
                                    <span className={`absolute -left-3 top-1 h-1.5 w-1.5 rounded-full ${eventColor(ev.event).replace("text-", "bg-")}`} />
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className={`${eventColor(ev.event)} capitalize text-[11px] font-medium`}>{(ev.event || "update").replace(/_/g, " ")}{ev.attempt && <span className="ml-1 text-[9px] text-gray-500 font-normal">· send attempt</span>}</p>
                                        {ev.reason && <p className="text-[10px] text-gray-500 truncate" title={ev.reason}>{ev.reason}</p>}
                                      </div>
                                      <p className="text-[10px] text-gray-600 whitespace-nowrap">{fmtEventTs(ev.ts)}</p>
                                    </div>
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
